import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useForm } from 'react-hook-form';
import api from '../../api/client';
import { describeError } from '../../api/errors';
import toast from 'react-hot-toast';
import StatusBadge from '../../components/shared/StatusBadge';
import StaffLink from '../../components/shared/StaffLink';
import {
  PlusIcon, XMarkIcon, FlagIcon, CheckCircleIcon, EyeIcon,
} from '@heroicons/react/24/outline';
import { DATE_FLOOR } from '../../lib/dateBounds';

const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

export default function OneOnOnesPage() {
  const { isManagement } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [template, setTemplate] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [m, s, t] = await Promise.all([
        api.get('/one-on-ones'),
        api.get('/staff'),
        api.get('/one-on-ones/template'),
      ]);
      setMeetings(m.data); setStaffList(s.data); setTemplate(t.data);
    } catch { toast.error('Failed to load 1:1s'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createForm = useForm();
  const create = async (form) => {
    try {
      const { data } = await api.post('/one-on-ones', {
        ...form,
        staff_user_id: Number(form.staff_user_id),
      });
      toast.success('Meeting scheduled');
      setShowCreate(false); createForm.reset(); load();
      setActiveId(data.id);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;

  return (
    <div className="animate-slide-up">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">1:1 Meetings</h1>
          <p className="text-gray-500 text-sm">{meetings.length} total · monthly check-ins</p>
        </div>
        {isManagement && (
          <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-xl hover:bg-orange-600 font-medium">
            <PlusIcon className="w-5 h-5" /> Schedule 1:1
          </button>
        )}
      </div>

      {meetings.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
          <p className="text-gray-500">No meetings yet</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs font-semibold text-gray-500 uppercase">
                <th className="px-6 py-3">Staff</th>
                <th className="px-6 py-3">Date</th>
                <th className="px-6 py-3">Conductor</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Next Check-In</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {meetings.map(m => (
                <tr key={m.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setActiveId(m.id)}>
                  <td className="px-6 py-3 text-sm font-medium text-gray-900">
                    <StaffLink id={m.staff_user_id} name={m.staff_name} />
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-600">{formatDate(m.meeting_date)}</td>
                  <td className="px-6 py-3 text-sm text-gray-600">{m.conductor_name || '—'}</td>
                  <td className="px-6 py-3"><StatusBadge status={m.status} /></td>
                  <td className="px-6 py-3 text-sm text-gray-600">{formatDate(m.next_check_in_date)}</td>
                  <td className="px-6 py-3 text-right">
                    <EyeIcon className="w-4 h-4 text-gray-400 inline" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <Modal title="Schedule 1:1 Meeting" onClose={() => setShowCreate(false)}>
          <form onSubmit={createForm.handleSubmit(create)} className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Staff *</label>
              <select {...createForm.register('staff_user_id', { required: true })}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm bg-white focus:border-orange-500 focus:outline-none">
                <option value="">Select…</option>
                {staffList.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Date</label>
                <input type="date" min={DATE_FLOOR} {...createForm.register('meeting_date')}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Classroom</label>
                <input {...createForm.register('classroom')}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Agenda (optional)</label>
              <textarea {...createForm.register('agenda')} rows={2}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none resize-none" />
            </div>
            <div className="flex gap-2 pt-2">
              <button type="submit" className="flex-1 bg-orange-500 text-white py-2.5 rounded-xl font-semibold hover:bg-orange-600">Schedule</button>
              <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600">Cancel</button>
            </div>
          </form>
        </Modal>
      )}

      {activeId && <MeetingModal id={activeId} template={template} onClose={() => setActiveId(null)} onChange={load} />}
    </div>
  );
}

function MeetingModal({ id, template, onClose, onChange }) {
  const { user, isManagement } = useAuth();
  const [meeting, setMeeting] = useState(null);
  const [context, setContext] = useState(null);
  const [responses, setResponses] = useState({});
  const [newGoal, setNewGoal] = useState('');
  // Persistent, in-modal failure banner. A 3-second toast at the top-right is
  // not an adequate signal that an hour of typing didn't save.
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/one-on-ones/${id}`);
      setMeeting(data);
      const r = {};
      for (const x of data.responses || []) r[x.prompt_id] = x.response || '';
      setResponses(r);
      try {
        const ctx = await api.get(`/one-on-ones/staff/${data.staff_user_id}/context`);
        setContext(ctx.data);
      } catch { /* ok */ }
    } catch { toast.error('Failed to load meeting'); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const isStaffMe = meeting?.staff_user_id === user.id;
  const canFill = isManagement || isStaffMe;

  const saveResponses = async () => {
    setSaveError(null);
    // The payload is built from `template`, NOT from what was typed. If the
    // template failed to load, this would POST an empty array, get a 200, and
    // cheerfully report "Saved" while persisting nothing. Refuse instead.
    const prompts = template.flatMap(s => s.prompts);
    if (prompts.length === 0) {
      console.error('saveResponses aborted: question template is empty, refusing to report a false save');
      setSaveError('The question list did not load, so there was nothing to save. Reload the page and try again — do NOT close this window, your answers below are still here.');
      toast.error('Nothing to save — reload the page');
      return;
    }
    setSaving(true);
    try {
      const payload = prompts.map(p => ({
        prompt_id: p.id, response: responses[p.id] || null,
      }));
      await api.post(`/one-on-ones/${id}/responses`, { responses: payload });
      toast.success('Saved'); setSaveError(null); load();
    } catch (err) {
      console.error('Failed to save 1:1 responses:', err);
      setSaveError(describeError(err, 'Could not save your answers.'));
      toast.error('Save failed — details in the window');
    } finally {
      setSaving(false);
    }
  };

  const conduct = async () => {
    const next_check_in_date = window.prompt('Next check-in date (YYYY-MM-DD, optional):');
    try {
      await api.patch(`/one-on-ones/${id}/conduct`, { next_check_in_date: next_check_in_date || null });
      toast.success('Marked as conducted'); setSaveError(null);
      load(); onChange?.();
    } catch (err) {
      console.error('Failed to mark 1:1 conducted:', err);
      setSaveError(describeError(err, 'Could not mark this meeting as conducted.'));
      toast.error('Failed — details in the window');
    }
  };

  const verify = async () => {
    try {
      await api.patch(`/one-on-ones/${id}/staff-verify`, { staff_response: 'Acknowledged' });
      toast.success('Verified'); setSaveError(null); load(); onChange?.();
    } catch (err) {
      console.error('Failed to verify 1:1:', err);
      setSaveError(describeError(err, 'Could not verify this meeting.'));
      toast.error('Failed — details in the window');
    }
  };

  const addGoal = async () => {
    if (!newGoal.trim()) {
      setSaveError('Type the goal first, then click Add.');
      return;
    }
    try {
      await api.post(`/one-on-ones/${id}/goals`, { body: newGoal });
      setNewGoal(''); setSaveError(null); load();
    } catch (err) {
      console.error('Failed to add 1:1 goal:', err);
      setSaveError(describeError(err, 'Could not add that goal.'));
      toast.error('Failed — details in the window');
    }
  };

  const closeGoal = async (goalId, status) => {
    try {
      await api.patch(`/one-on-ones/goals/${goalId}`, { status, closed_in_meeting_id: id });
      setSaveError(null); load();
    } catch (err) {
      console.error('Failed to close 1:1 goal:', err);
      setSaveError(describeError(err, 'Could not update that goal.'));
      toast.error('Failed — details in the window');
    }
  };

  if (!meeting) {
    return (
      <Modal title="Loading…" onClose={onClose} maxWidth="max-w-3xl">
        <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>
      </Modal>
    );
  }

  return (
    <Modal title={`1:1 — ${meeting.staff_first} ${meeting.staff_last}`} onClose={onClose} maxWidth="max-w-4xl">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Failure banner — stays put until the next successful action. A
            transient corner toast is not enough when an hour of typing is at
            stake, and it can be missed entirely behind this modal. */}
        {saveError && (
          <div className="lg:col-span-3 bg-red-50 border-2 border-red-300 rounded-xl p-4" role="alert">
            <p className="text-sm font-bold text-red-800 mb-1">⚠️ Not saved</p>
            <p className="text-sm text-red-700">{saveError}</p>
          </div>
        )}

        {/* Auto-context column */}
        <aside className="lg:col-span-1 space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
            <p className="text-xs font-semibold text-amber-700 mb-2">Last 35 days</p>
            <p className="text-xs text-amber-900">
              <strong>{context?.point_balance != null ? Number(context.point_balance).toFixed(1) : '—'}</strong> attendance points
            </p>
            <p className="text-xs text-amber-900">
              <strong>{context?.attendance?.length || 0}</strong> attendance events
            </p>
            <p className="text-xs text-amber-900">
              <strong>{context?.observations?.length || 0}</strong> observations
            </p>
            <p className="text-xs text-amber-900">
              <strong>{context?.discipline?.length || 0}</strong> write-ups
            </p>
          </div>

          {meeting.open_goals?.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
              <p className="text-xs font-semibold text-blue-700 mb-2 flex items-center gap-1">
                <FlagIcon className="w-3.5 h-3.5" /> Open Goals (carry-forward)
              </p>
              <ul className="space-y-2">
                {meeting.open_goals.map(g => (
                  <li key={g.id} className="text-xs text-blue-900">
                    <p>{g.body}</p>
                    <p className="text-blue-600/70">Set {formatDate(g.set_in_meeting_date || g.created_at)}</p>
                    {isManagement && (
                      <div className="flex gap-1 mt-1">
                        <button onClick={() => closeGoal(g.id, 'met')} className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Met</button>
                        <button onClick={() => closeGoal(g.id, 'partial')} className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Partial</button>
                        <button onClick={() => closeGoal(g.id, 'dropped')} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">Dropped</button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {meeting.prior_meeting && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
              <p className="text-xs font-semibold text-gray-700 mb-1">Last meeting</p>
              <p className="text-xs text-gray-600">{formatDate(meeting.prior_meeting.meeting_date)}</p>
              {meeting.prior_meeting.agenda && <p className="text-xs text-gray-600 mt-1">"{meeting.prior_meeting.agenda}"</p>}
            </div>
          )}

          {meeting.employee_prefill_at && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-xs text-green-800">
              <CheckCircleIcon className="w-4 h-4 inline mr-1" />
              Employee pre-filled on {new Date(meeting.employee_prefill_at).toLocaleDateString()}
            </div>
          )}
        </aside>

        {/* Form */}
        <div className="lg:col-span-2 space-y-4">
          <div className="text-sm text-gray-600 grid grid-cols-2 gap-3">
            <div><strong>Date:</strong> {formatDate(meeting.meeting_date)}</div>
            <div><strong>Classroom:</strong> {meeting.classroom || '—'}</div>
            <div><strong>Conductor:</strong> {meeting.conductor_first} {meeting.conductor_last}</div>
            <div><strong>Status:</strong> <StatusBadge status={meeting.status} /></div>
          </div>

          {template.map(section => (
            <div key={section.id} className="border border-gray-100 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">{section.label}</h3>
              <div className="space-y-3">
                {section.prompts.map(p => (
                  <div key={p.id}>
                    <label className="block text-xs text-gray-600 mb-1">{p.prompt_text}</label>
                    <textarea value={responses[p.id] || ''} disabled={!canFill || meeting.is_locked}
                      onChange={(e) => setResponses({ ...responses, [p.id]: e.target.value })}
                      rows={2}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none resize-none disabled:bg-gray-50" />
                  </div>
                ))}
              </div>
            </div>
          ))}

          {isManagement && (
            <div className="border border-blue-200 rounded-xl p-3 bg-blue-50/30">
              <label className="block text-xs font-semibold text-blue-700 mb-1">Add Goal Before Next Check-In</label>
              <div className="flex gap-2">
                <input value={newGoal} onChange={(e) => setNewGoal(e.target.value)}
                  placeholder="e.g. Practice the calming techniques discussed today"
                  className="flex-1 rounded-lg border border-blue-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none" />
                <button onClick={addGoal} className="bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-600">Add</button>
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-3 border-t border-gray-100">
            {canFill && (
              <button onClick={saveResponses} disabled={saving} className="bg-gray-100 text-gray-700 px-4 py-2 rounded-xl text-sm font-medium hover:bg-gray-200 disabled:opacity-50">
                {saving ? 'Saving…' : 'Save Responses'}
              </button>
            )}
            {isManagement && meeting.status === 'scheduled' && (
              <button onClick={conduct} className="bg-orange-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-orange-600">
                Mark as Conducted
              </button>
            )}
            {isStaffMe && meeting.status === 'conducted' && (
              <button onClick={verify} className="bg-green-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-green-600">
                Acknowledge
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

function Modal({ title, onClose, maxWidth = 'max-w-md', children }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-2xl shadow-xl w-full ${maxWidth} max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose}><XMarkIcon className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
